import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import type { components, paths } from '../schema';
import type { FieldValue } from '../types';
import type { Pretty, RequestOptions, RequestOptionsWithParams } from './types';
import { curry1 } from './utils';

export type DisplayNamesRequest = Exclude<
  paths['/api/records/display-names']['post']['requestBody'],
  undefined
>['content']['application/json'];

export type DisplayNamesResponse =
  paths['/api/records/display-names']['post']['responses']['200']['content']['application/json'];

export type SearchEntityRecordsOptions = RequestOptionsWithParams<
  paths['/api/records/{object_identifier}/search']['post']['parameters']['query']
> & {
  body?: Required<
    paths['/api/records/{object_identifier}/search']['post']
  >['requestBody']['content']['application/json'];
};

export type SearchEntityRecordsResponse = Omit<
  paths['/api/records/{object_identifier}/search']['post']['responses']['200']['content']['application/json'],
  'results'
> & {
  results: EntityRecordDetail[];
};

export type FieldValueResponse = Omit<
  paths['/api/records/{entity_id}/field-values/{field_id}']['get']['responses']['200']['content']['application/json'],
  'results'
> & {
  results: FieldValue[];
};

export type EntityRecordRequest =
  paths['/api/records/{object_identifier}/{entity_id}']['get']['parameters']['query'];

export type FieldValueParams =
  paths['/api/records/{entity_id}/field-values/{field_id}']['get']['parameters']['query'];

export type EntityRecordDetail = Pretty<
  Omit<components['schemas']['EntityRecordDetail'], 'fields'> & {
    fields: Record<string, FieldValue>;
  }
>;

export type PatchRecordResponse =
  paths['/api/records/{object_identifier}/{entity_id}']['patch']['responses']['200']['content']['application/json'];

export const createEntityRecord = async (
  instance: AxiosInstance,
  objectId: string,
  data: paths['/api/records/{object_identifier}/add']['post']['requestBody']['content']['application/json'],
  options: RequestOptions
): Promise<EntityRecordDetail> => {
  const res = await instance.post<EntityRecordDetail>(
    `/records/${objectId}/add`,
    data,
    options
  );

  return res.data;
};

export const getDisplayNames = async (
  instance: AxiosInstance,
  data: DisplayNamesRequest,
  options?: RequestOptions
): Promise<DisplayNamesResponse> => {
  const res = await instance.post<DisplayNamesResponse>(
    '/records/display-names',
    data,
    options
  );

  return res.data;
};

export const getEntityRecord = async (
  instance: AxiosInstance,
  objectId: string,
  entityId: string,
  options?: RequestOptionsWithParams<EntityRecordRequest>
): Promise<EntityRecordDetail> => {
  const res = await instance.get<EntityRecordDetail>(
    `/records/${objectId}/${entityId}`,
    options
  );

  return res.data;
};

export const patchEntityRecord = async (
  instance: AxiosInstance,
  objectId: string,
  entityId: string,
  data: Required<
    paths['/api/records/{object_identifier}/{entity_id}']['patch']
  >['requestBody']['content']['application/json'],
  options: RequestOptionsWithParams<
    paths['/api/records/{object_identifier}/{entity_id}']['patch']['parameters']['query']
  >
): Promise<PatchRecordResponse> => {
  const res = await instance.patch<PatchRecordResponse>(
    `/records/${objectId}/${entityId}`,
    data,
    options
  );

  return res.data;
};

export const searchEntityRecords = async (
  instance: AxiosInstance,
  objectId: string,
  options: SearchEntityRecordsOptions = {}
): Promise<SearchEntityRecordsResponse> => {
  const { body, params, ...opts } = options;

  const config: AxiosRequestConfig = { ...opts, params };

  const res = await instance.post<SearchEntityRecordsResponse>(
    `/records/${objectId}/search`,
    body,
    config
  );

  return res.data;
};

export const getFieldValues = async (
  instance: AxiosInstance,
  entityId: string,
  fieldId: string,
  options: RequestOptionsWithParams<FieldValueParams> = {}
): Promise<FieldValueResponse> => {
  const { params, ...opts } = options;

  const config: AxiosRequestConfig = { ...opts, params };

  const res = await instance.get<FieldValueResponse>(
    `/records/${entityId}/field-values/${fieldId}`,
    config
  );

  return res.data;
};

export class RecordsService {
  public createEntityRecord;
  public getDisplayNames;
  public patchEntityRecord;
  public searchEntityRecords;
  public getFieldValues;

  constructor(private instance: AxiosInstance) {
    this.createEntityRecord = curry1(createEntityRecord)(this.instance);
    this.getDisplayNames = curry1(getDisplayNames)(this.instance);
    this.patchEntityRecord = curry1(patchEntityRecord)(this.instance);
    this.searchEntityRecords = curry1(searchEntityRecords)(this.instance);
    this.getFieldValues = curry1(getFieldValues)(this.instance);
  }
}
